import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Ruler, Activity, ChevronRight } from 'lucide-react';

interface FFMICalculatorProps {
  latestWeight: number | null;
  latestBodyFat: number | null;
  onProfileChange: () => void;
}

interface Classification {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  barColor: string;
  desc: string;
  min: number;
  max: number;
}

const CLASSIFICATIONS: Classification[] = [
  { label: 'Below Average', color: 'text-muted-foreground', bgColor: 'bg-muted/40', borderColor: 'border-muted-foreground/30', barColor: 'bg-muted-foreground/50', desc: 'Slightly below average muscle density for active individuals.', min: 0, max: 18 },
  { label: 'Average',       color: 'text-foreground',       bgColor: 'bg-secondary/60',  borderColor: 'border-border',                barColor: 'bg-foreground/40',         desc: 'Standard healthy muscle density for active individuals.',           min: 18, max: 20 },
  { label: 'Excellent',     color: 'text-success',          bgColor: 'bg-success/8',     borderColor: 'border-success/40',            barColor: 'bg-success',               desc: 'Distinctly athletic build with notable muscularity.',               min: 20, max: 22 },
  { label: 'Superior',      color: 'text-primary',          bgColor: 'bg-primary/8',     borderColor: 'border-primary/40',            barColor: 'bg-primary',               desc: 'Highly developed physique. Near natural genetic ceiling.',           min: 22, max: 23 },
  { label: 'Genetic Limit', color: 'text-primary font-bold', bgColor: 'bg-primary/12',  borderColor: 'border-primary/60',            barColor: 'bg-primary',               desc: 'Exceptional muscularity. Top-tier natural bodybuilding range.',      min: 23, max: 25 },
  { label: 'Enhanced / Elite', color: 'text-destructive font-bold', bgColor: 'bg-destructive/8', borderColor: 'border-destructive/40', barColor: 'bg-destructive',       desc: 'Muscularity beyond standard natural limits.',                       min: 25, max: 30 },
];

function getClassification(score: number): Classification {
  return CLASSIFICATIONS.find(c => score < c.max) ?? CLASSIFICATIONS[CLASSIFICATIONS.length - 1];
}

// Progress bar: maps FFMI 14–26 to 0–100%
function ffmiToPercent(score: number) {
  return Math.min(100, Math.max(0, ((score - 14) / 12) * 100));
}

export default function FFMICalculator({ latestWeight, latestBodyFat, onProfileChange }: FFMICalculatorProps) {
  const [feet, setFeet] = useState('5');
  const [inches, setInches] = useState('10');
  const [isEditingHeight, setIsEditingHeight] = useState(false);

  const utils = trpc.useUtils();
  const { data: profile } = trpc.profile.get.useQuery();

  const saveProfileMutation = trpc.profile.save.useMutation({
    onSuccess: () => {
      utils.profile.get.invalidate();
      onProfileChange();
    },
  });

  useEffect(() => {
    if (profile) {
      if (profile.heightFeet !== null && profile.heightFeet !== undefined) setFeet(profile.heightFeet.toString());
      if (profile.heightInches !== null && profile.heightInches !== undefined) setInches(profile.heightInches.toString());
    }
  }, [profile]);

  const handleSaveHeight = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedFeet = parseInt(feet);
    const parsedInches = parseInt(inches);
    if (isNaN(parsedFeet) || parsedFeet < 3 || parsedFeet > 8) {
      toast.error('Please enter a valid height (feet between 3 and 8)');
      return;
    }
    if (isNaN(parsedInches) || parsedInches < 0 || parsedInches >= 12) {
      toast.error('Please enter valid inches (between 0 and 11)');
      return;
    }
    await saveProfileMutation.mutateAsync({ heightFeet: parsedFeet, heightInches: parsedInches });
    toast.success(`Height updated to ${parsedFeet}' ${parsedInches}"`);
    setIsEditingHeight(false);
  };

  const heightInMeters = ((parseInt(feet) * 12) + parseInt(inches)) * 0.0254;
  const weightInKg = latestWeight ? latestWeight * 0.45359237 : null;

  let leanMassLbs: string | null = null;
  let fatMassLbs: string | null = null;
  let ffmi: number | null = null;
  let normalizedFfmi: number | null = null;

  if (weightInKg && latestBodyFat !== null && latestWeight !== null && heightInMeters > 0) {
    const bodyFatFraction = latestBodyFat / 100;
    const leanMassKg = weightInKg * (1 - bodyFatFraction);
    leanMassLbs = (leanMassKg * 2.20462).toFixed(1);
    fatMassLbs = (latestWeight * bodyFatFraction).toFixed(1);
    ffmi = leanMassKg / (heightInMeters * heightInMeters);
    normalizedFfmi = ffmi + 6.1 * (1.8 - heightInMeters);
  }

  const classification = normalizedFfmi !== null ? getClassification(normalizedFfmi) : null;
  const barPercent = normalizedFfmi !== null ? ffmiToPercent(normalizedFfmi) : 0;

  const hasData = latestWeight !== null && latestBodyFat !== null;

  return (
    <div className="forge-panel overflow-hidden">
      {/* Hero FFMI Score */}
      <div className={`relative p-5 ${hasData && classification ? classification.bgColor : 'bg-secondary/30'} border-b border-border/50`}>
        <div className="flex items-start justify-between mb-1">
          <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
            <Activity className="h-3 w-3 text-primary" />
            Fat-Free Mass Index
          </div>
          {hasData && classification && (
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${classification.color} ${classification.borderColor} bg-background/60`}>
              {classification.label}
            </span>
          )}
        </div>

        {hasData && normalizedFfmi !== null ? (
          <>
            <div className="flex items-baseline gap-2 mt-1">
              <span className={`text-5xl font-mono font-black tracking-tight ${classification?.color ?? 'text-foreground'}`}>
                {normalizedFfmi.toFixed(1)}
              </span>
              <span className="text-xs font-mono text-muted-foreground mb-1">NORMALIZED</span>
            </div>
            {classification && (
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{classification.desc}</p>
            )}

            {/* Progress bar */}
            <div className="mt-3 space-y-1">
              <div className="h-1.5 bg-border/60 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${classification?.barColor ?? 'bg-primary'}`}
                  style={{ width: `${barPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-[9px] font-mono text-muted-foreground/60">
                <span>14 — Below Avg</span>
                <span>20 — Excellent</span>
                <span>25+ — Elite</span>
              </div>
            </div>
          </>
        ) : (
          <div className="mt-2 space-y-1">
            <div className="text-4xl font-mono font-black text-muted-foreground/30">—.—</div>
            <p className="text-[11px] text-muted-foreground">
              Log <strong className="text-foreground">Weight</strong> + <strong className="text-foreground">Body Fat %</strong> to calculate your FFMI score.
            </p>
          </div>
        )}
      </div>

      {/* Body Composition Breakdown */}
      {hasData && leanMassLbs && fatMassLbs && (
        <div className="grid grid-cols-2 divide-x divide-border/50 border-b border-border/50">
          <div className="p-3 space-y-0.5">
            <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Lean Mass</div>
            <div className="text-xl font-mono font-bold text-success">{leanMassLbs}<span className="text-xs font-normal text-muted-foreground ml-1">LBS</span></div>
            <div className="text-[9px] text-muted-foreground">Muscle, bone &amp; organs</div>
          </div>
          <div className="p-3 space-y-0.5">
            <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Fat Mass</div>
            <div className="text-xl font-mono font-bold text-destructive">{fatMassLbs}<span className="text-xs font-normal text-muted-foreground ml-1">LBS</span></div>
            <div className="text-[9px] text-muted-foreground">Adipose storage</div>
          </div>
        </div>
      )}

      {/* Height Config */}
      <div className="p-4">
        {isEditingHeight ? (
          <form onSubmit={handleSaveHeight} className="space-y-3 p-3 bg-secondary/20 border border-border/40 rounded-sm">
            <div className="text-[10px] font-mono text-muted-foreground uppercase flex items-center gap-1">
              <Ruler className="h-3.5 w-3.5 text-primary" /> Configure Stature
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Feet</label>
                <Input type="number" value={feet} onChange={(e) => setFeet(e.target.value)} className="bg-secondary border-border font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Inches</label>
                <Input type="number" value={inches} onChange={(e) => setInches(e.target.value)} className="bg-secondary border-border font-mono text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={saveProfileMutation.isPending} className="flex-1 bg-primary text-primary-foreground font-mono text-xs">SAVE</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setIsEditingHeight(false)} className="flex-1 font-mono text-xs">CANCEL</Button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setIsEditingHeight(true)}
            className="w-full flex items-center justify-between p-2.5 bg-secondary/30 border border-border/40 rounded-sm hover:bg-secondary/50 transition-colors group"
          >
            <div className="space-y-0.5 text-left">
              <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Stature</div>
              <div className="text-sm font-mono font-bold text-foreground">
                {feet}' {inches}" <span className="text-[10px] font-normal text-muted-foreground">({(heightInMeters * 100).toFixed(0)} cm)</span>
              </div>
            </div>
            <div className="flex items-center gap-1 text-[10px] font-mono text-primary/70 group-hover:text-primary transition-colors">
              <Ruler className="h-3 w-3" />
              EDIT
              <ChevronRight className="h-3 w-3" />
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
